import React, { useContext, useState } from 'react';
import { Alert, Box, CircularProgress, Fab, FormControl, InputLabel, Link, MenuItem, Paper, Select, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DialPad, EmergencyBanner, OngoingCall, isIncomingRinging, useSoftphone } from '@dialstack/sdk-react/softphone';
import { CallFilledIcon } from 'components/mui';
import { SoftphoneContext } from 'context/softphone-context';

// Above the app chrome but below modals, whose z-index is 900 + 100 * level.
const PANEL_Z_INDEX = 850;

/**
 * The SDK's <Softphone> minus its ringing screen: the call pop is where an incoming call
 * is shown and answered, so having the SDK show it too would be a second notice for the
 * same call. Mirrors what <Softphone> switches between otherwise.
 *
 * Must render under the SDK's provider, which is why it is only mounted while connected.
 */
const SoftphoneScreens = () => {
  const {
    calls,
    activeCall,
    scope
  } = useSoftphone();

  // A held call counts as being on a call too, the same rule <Softphone> uses.
  const onCall = calls.some(call => call === activeCall || !isIncomingRinging(call));
  if (onCall) {
    return <OngoingCall />;
  }

  // The scoped wrapper is what the SDK's stylesheet keys the dial screen's layout on.
  return <div className={`${scope} ds-dial-screen`}>
      <EmergencyBanner />
      <DialPad autoFocusDestination />
    </div>;
};

/**
 * The floating softphone: a launcher button, and a panel holding the SDK's own UI.
 *
 * Only the panel unmounts when closed — the connection lives in SoftphoneContextProvider,
 * so the phone stays registered and can still ring while this is shut.
 *
 * It can also be hidden outright, but only while disconnected: there is no call to miss
 * then, and the launcher is just something in the corner. Deliberately held in state rather
 * than stored, so a refresh brings it back and nobody loses their phone permanently by
 * clicking a link once.
 */
export const SoftphonePanel = () => {
  const {
    enabled,
    phones,
    phone,
    choosePhone,
    disconnect,
    connected,
    loading,
    error,
    isOpen,
    toggle,
    close
  } = useContext(SoftphoneContext) || {};
  const [hiddenUntilRefresh, setHiddenUntilRefresh] = useState(false);
  if (!enabled || hiddenUntilRefresh) {
    return null;
  }
  return <>
      <Tooltip title={connected ? `Connected as ${phone?.name}` : 'Softphone — not connected'}>
        <Fab size="medium" onClick={toggle} sx={{
        'position': 'fixed',
        'bottom': '24px',
        'right': '24px',
        'zIndex': PANEL_Z_INDEX,
        'backgroundColor': connected ? '#48AD48' : '#53A6D6',
        'color': '#fff',
        '&:hover': {
          backgroundColor: connected ? '#48AD48' : '#53A6D6'
        }
      }}>
          <CallFilledIcon fill="#fff" />
        </Fab>
      </Tooltip>

      {isOpen && <Paper elevation={8} sx={{
      position: 'fixed',
      bottom: '88px',
      right: '24px',
      zIndex: PANEL_Z_INDEX,
      width: '360px',
      maxHeight: '640px',
      overflow: 'auto',
      borderRadius: '12px',
      p: 2
    }}>
          <Stack direction="row" sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
            <Typography sx={{
          fontWeight: 600
        }}>Softphone</Typography>
            <CloseIcon sx={{
          cursor: 'pointer'
        }} onClick={close} />
          </Stack>

          {/* Rendered whenever there is a phone at all, not just when there's a choice
              between several: it's also how you disconnect. */}
          {phones.length > 0 && <FormControl fullWidth size="small" sx={{
        pb: 2
      }}>
              <InputLabel id="softphone-phone-label">Phone</InputLabel>
              <Select labelId="softphone-phone-label" label="Phone" value={phone?.id || ''} onChange={event => event.target.value ? choosePhone(event.target.value) : disconnect()}>
                {/* First, so hanging up is the one option whose position never moves as
                    phones come and go. The empty value is the disconnected state, so it
                    doubles as the way out and as what the field shows while off. */}
                <MenuItem value="">{connected ? 'Disconnect' : 'Not connected'}</MenuItem>

                {phones.map(option => <MenuItem key={option.id} value={option.id}>
                    {option.location_name ? `${option.name} — ${option.location_name}` : option.name}
                  </MenuItem>)}
              </Select>
            </FormControl>}

          {phones.length === 0 && <Alert severity="warning">
              You don't have access to any phones on this account. Ask an admin to add one in Cinch Phones, or to set the location on an
              existing phone.
            </Alert>}

          {phones.length > 0 && !phone && <Alert severity="info">Choose which phone this browser should answer as. It will ring here until you disconnect.</Alert>}

          {loading && <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 3
      }}>
              <CircularProgress size={24} />
            </Box>}

          {error && <Alert severity="error">{error}</Alert>}

          {/* The SDK renders the dial pad and the in-call controls; the incoming-call
              screen is the call pop's job. */}
          {connected && !loading && <SoftphoneScreens />}

          {/* Only offered while disconnected — hiding a connected phone would mean an
              incoming call ringing with nothing on screen to answer it with. */}
          {!connected && <Box sx={{
        pt: 2,
        textAlign: 'center'
      }}>
              <Link component="button" type="button" variant="body2" underline="hover" onClick={() => setHiddenUntilRefresh(true)} sx={{
          color: '#53A6D6'
        }}>
                Hide icon until refresh
              </Link>
            </Box>}
        </Paper>}
    </>;
};
export default SoftphonePanel;